import { z } from 'zod'

import { jsonCodec } from './zod-extra'

/**
 * See https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
export enum WebsocketStatusCode {
  NormalClosure = 1000,
  PolicyViolation = 1008,
  InternalError = 1011,
  ServiceRestart = 1012
}

export enum GenerationTaskStatus {
  Completed = 'Completed',
  Failed = 'Failed',
  Processing = 'Processing',
  Scheduled = 'Scheduled'
}

export namespace PanoramaWebSocketApi {
  export type RequestPayloadSchema = z.ZodObject<{ action: z.ZodLiteral }>
  export type ResponsePayloadSchema = z.ZodObject<{ action: z.ZodLiteral }>
  /** Intermediate data may be a discriminated union when multiple event shapes share one action. */
  export type ResponsePayloadUnionSchema = z.ZodDiscriminatedUnion<readonly z.ZodTypeAny[], string>
  export type AnyResponsePayloadSchema = ResponsePayloadSchema | ResponsePayloadUnionSchema
  export type ResponseEnvelopeSchema<
    MessageType extends string = string,
    DataSchema extends z.ZodTypeAny = ResponsePayloadSchema
  > = z.ZodObject<{
    requestId: z.ZodString
    messageType: z.ZodLiteral<MessageType>
    data: DataSchema
  }>

  export type AnyDefinition = Definition<
    RequestPayloadSchema,
    ResponseEnvelopeSchema<'terminal', ResponsePayloadSchema>,
    ResponseEnvelopeSchema<'intermediate', AnyResponsePayloadSchema> | z.ZodNever
  >
  export type RequestPayload<TDef extends AnyDefinition> = z.infer<TDef['requestSchema']>

  export type TerminalMessage<TDef extends AnyDefinition> = z.infer<TDef['terminalSchema']>
  export type TerminalPayload<TDef extends AnyDefinition> = TerminalMessage<TDef>['data']

  export type IntermediateMessage<TDef extends AnyDefinition> = z.infer<
    NonNullable<TDef['intermediateSchema']>
  >
  export type IntermediatePayload<TDef extends AnyDefinition> = IntermediateMessage<TDef>['data']

  export type Definition<
    RequestSchema extends RequestPayloadSchema,
    TerminalSchema extends ResponseEnvelopeSchema<'terminal', ResponsePayloadSchema>,
    IntermediateSchema extends
      | ResponseEnvelopeSchema<'intermediate', AnyResponsePayloadSchema>
      | z.ZodNever = z.ZodNever
  > = {
    type: 'Request' | 'Context'
    requestSchema: RequestSchema
    terminalSchema: TerminalSchema
    intermediateSchema?: IntermediateSchema
    timeoutSeconds?: number
  }

  export const defineApi = <
    RequestSchema extends RequestPayloadSchema,
    TerminalSchema extends ResponseEnvelopeSchema<'terminal', ResponsePayloadSchema>,
    IntermediateSchema extends
      | ResponseEnvelopeSchema<'intermediate', AnyResponsePayloadSchema>
      | z.ZodNever = z.ZodNever
  >(
    def: Definition<RequestSchema, TerminalSchema, IntermediateSchema>
  ) => def

  export const createResponseEnvelopeSchema = <
    MessageType extends 'terminal' | 'intermediate',
    PayloadSchema extends z.ZodTypeAny
  >({
    messageType,
    data
  }: {
    messageType: MessageType
    data: PayloadSchema
  }): ResponseEnvelopeSchema<MessageType, PayloadSchema> =>
    z.object({
      requestId: z.string(),
      messageType: z.literal(messageType),
      data
    })

  /**
   * Constructs a schema for Aurora WebSocket responses that follow the
   * standard pattern of returning a status along with either data or an error message.
   */
  export const createResponseSchema = <SuccessSchema extends z.ZodType>(response: SuccessSchema) =>
    jsonCodec(
      z.discriminatedUnion('status', [
        z.object({
          status: z.literal(GenerationTaskStatus.Completed), // TODO: consider Kevin's suggestion of using action instead of status for this. See comments on https://git.2020.dev/panorama/panorama/-/merge_requests/2502
          requestId: z.string(),
          data: response
        }),
        z.object({
          requestId: z.string(),
          status: z.literal(GenerationTaskStatus.Processing),
          data: z.unknown()
        }),

        z.object({
          requestId: z.string(),
          status: z.literal(GenerationTaskStatus.Failed),
          error: z.string()
        })
      ])
    )

  export type Response<SuccessShape = unknown> = z.infer<
    ReturnType<typeof createResponseSchema<z.ZodType<SuccessShape>>>
  >
}
